import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ShieldCheck, EyeOff, Lock, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

/**
 * PrivacyAssuranceLink — small, subtle inline privacy reassurance.
 * Shows a tiny shield + "Your privacy is sacred · See how →" link.
 * Clicking opens a Dialog with the plain-English privacy summary.
 *
 * Designed to be easy to find but NOT the main focus of any page.
 */
export default function PrivacyAssuranceLink({
  variant = "light", // "light" (cream bg) or "dark" (dark bg)
  className = "",
}) {
  const [open, setOpen] = useState(false);

  const isDark = variant === "dark";

  return (
    <>
      <button
        type="button"
        data-testid="privacy-assurance-link"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 font-body text-[12px] transition-colors ${
          isDark
            ? "text-white/60 hover:text-white"
            : "text-[#6b7280] hover:text-[#3e6b52]"
        } ${className}`}
      >
        <Shield size={11} className={isDark ? "text-white/55" : "text-[#3e6b52]"} />
        <span>
          Your privacy is sacred ·{" "}
          <span className="underline underline-offset-2">See how</span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="privacy-assurance-dialog"
          className="max-w-md sm:max-w-lg p-0 gap-0 bg-[#f7f3ea] border-[#eadfc9] rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-white border-b border-[#eadfc9] px-6 py-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#e9f0ec] flex items-center justify-center shrink-0">
              <ShieldCheck size={18} className="text-[#3e6b52]" />
            </div>
            <div className="flex-1 min-w-0 pr-6">
              <DialogTitle className="font-display text-[20px] sm:text-[22px] text-[#0f1724] leading-tight">
                Your conversations stay yours.
              </DialogTitle>
              <DialogDescription className="font-body text-[13px] text-[#4b5563] mt-1 leading-relaxed">
                Ascend is a vault. Only you hold the key.
              </DialogDescription>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#6b7280] hover:bg-[#f7f3ea] transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-3.5">
            {[
              {
                icon: ShieldCheck,
                title: "Never sold",
                body: "Not to advertisers, data brokers, or anyone else. Ever.",
              },
              {
                icon: EyeOff,
                title: "Never shared",
                body: "Your private chats aren't used to train public AI or shown to other users.",
              },
              {
                icon: Lock,
                title: "Encrypted in transit",
                body: "TLS 1.2+. Stored on hardened cloud infra with restricted, audited access.",
              },
              {
                icon: Trash2,
                title: "Delete anytime",
                body: "One click, no forms. We delete personal data within 30 days.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-white border border-[#eadfc9] flex items-center justify-center shrink-0 mt-0.5">
                  <Icon size={13} className="text-[#3e6b52]" />
                </div>
                <div>
                  <p className="font-body text-[14px] font-semibold text-[#0f1724] leading-tight">
                    {title}
                  </p>
                  <p className="font-body text-[13px] text-[#4b5563] leading-relaxed mt-0.5">
                    {body}
                  </p>
                </div>
              </div>
            ))}

            <div className="pt-3 mt-3 border-t border-[#eadfc9]">
              <p className="font-body text-[12px] text-[#6b7280] leading-relaxed italic">
                The only times we will ever hand over your data are if you ask
                us to, or if we are legally compelled by a court order or credible
                imminent-harm safety report — and even then, only what's required.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-white border-t border-[#eadfc9] flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Link
              to="/privacy"
              data-testid="privacy-assurance-full-policy"
              onClick={() => setOpen(false)}
              className="flex-1 text-center px-4 py-2.5 rounded-full bg-[#0f1724] text-white font-body text-[13px] font-medium hover:bg-[#1c2944] transition-colors"
            >
              Read the full Privacy Policy
            </Link>
            <button
              onClick={() => setOpen(false)}
              data-testid="privacy-assurance-got-it"
              className="px-4 py-2.5 rounded-full bg-[#f7f3ea] text-[#2b3442] font-body text-[13px] font-medium hover:bg-[#f2ecdc] transition-colors border border-[#eadfc9]"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
